import { useEffect, useMemo, useState } from 'react';

import { LoadingIndicator } from '../components/LoadingIndicator';
import { MonthStepper } from '../components/MonthStepper';
import { MonthWednesdayStepper } from '../components/MonthWednesdayStepper';
import { PageHero } from '../components/PageHero';
import {
  createActivityPeriod,
  createAdminPerson,
  fetchAdminPeople,
  fetchAvailableDates,
  fetchPlanForDate,
  fetchPlanningPreviewForAppointmentDates,
  fetchStats,
  planAppointmentDates,
  removeAppointmentDatePlan,
  setAvailableDateAvailability,
  updateActivityPeriod,
  updateAdminSlot,
  type AdminPerson,
  type AppointmentDateRecord,
  type PlanSlot,
  type PlanningPreview,
  type StatsItem,
} from '../lib/api';
import {
  formatDate,
  getUpcomingWednesday,
  getWednesdaysForMonth,
} from '../lib/dates';

const today = new Date().toISOString().slice(0, 10);
const currentMonth = today.slice(0, 7);
const upcomingWednesday = getUpcomingWednesday(new Date());

type AdminTab = 'personen' | 'planung' | 'slot-editor' | 'statistik';
type MobileView =
  | 'start'
  | 'personen'
  | 'mittwoche'
  | 'planung'
  | 'slot-editor'
  | 'statistik';

type SlotDraft = {
  currentPersonId: number | null;
  wantsTrade: boolean;
};

type StatsSortKey =
  | 'displayName'
  | 'pastSlots'
  | 'slotDebtUntilToday'
  | 'futurePlannedSlots'
  | 'overallFairnessDebt';

type StatsSort = {
  direction: 'asc' | 'desc';
  key: StatsSortKey;
};

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Aktion fehlgeschlagen.';
}

function formatDebt(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
    signDisplay: 'exceptZero',
  }).format(value);
}

function sortIsoDates(dates: string[]): string[] {
  return [...new Set(dates)].sort((left, right) => left.localeCompare(right));
}

function isActiveToday(person: AdminPerson, referenceDate: string): boolean {
  return person.activityPeriods.some((period) => {
    const startsOk = period.startsOn <= referenceDate;
    const endsOk = period.endsOn === null || period.endsOn >= referenceDate;
    return startsOk && endsOk;
  });
}

function isGroupTrainingSlot(slot: PlanSlot): boolean {
  return slot.slotType === 'group_training';
}

function slotAssignmentLabel(
  slot: PlanSlot,
  personId: number | null,
  peopleById: Map<number, string>,
  emptyLabel = 'unbesetzt',
): string {
  return (
    slot.slotLabel ?? (personId ? peopleById.get(personId) : null) ?? emptyLabel
  );
}

function slotOriginalAssignmentLabel(
  slot: PlanSlot,
  emptyLabel = 'unbesetzt',
): string {
  return slot.slotLabel ?? slot.originalPersonName ?? emptyLabel;
}

function formatSlotGroupLabel(slotGroup: string): string {
  if (slotGroup === 'frueh') {
    return 'früh';
  }

  if (slotGroup === 'spaet') {
    return 'spät';
  }

  return slotGroup;
}

export function AdminPage() {
  const [people, setPeople] = useState<AdminPerson[]>([]);
  const [appointmentDates, setAppointmentDates] = useState<
    AppointmentDateRecord[]
  >([]);
  const [planningPreview, setPlanningPreview] =
    useState<PlanningPreview | null>(null);
  const [planningPreviewLoading, setPlanningPreviewLoading] = useState(false);
  const [planningPreviewError, setPlanningPreviewError] = useState<
    string | null
  >(null);
  const [planningPreviewRefreshKey, setPlanningPreviewRefreshKey] = useState(0);
  const [selectedPlanningDates, setSelectedPlanningDates] = useState<string[]>(
    [],
  );
  const [stats, setStats] = useState<StatsItem[]>([]);
  const [statsSort, setStatsSort] = useState<StatsSort | null>(null);
  const [slotsForAdminDate, setSlotsForAdminDate] = useState<PlanSlot[]>([]);
  const [slotDrafts, setSlotDrafts] = useState<Record<number, SlotDraft>>({});
  const [adminDate, setAdminDate] = useState(upcomingWednesday);
  const [availabilityMonth, setAvailabilityMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('personen');
  const [mobileView, setMobileView] = useState<MobileView>('start');
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia('(max-width: 768px)').matches,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [personForm, setPersonForm] = useState({
    displayName: '',
    notes: '',
    startsOn: today,
  });
  const [activityForm, setActivityForm] = useState({
    startsOn: today,
    endsOn: today,
  });
  const [editNote, setEditNote] = useState('');

  const appointmentDateByDate = useMemo(
    () =>
      new Map(
        appointmentDates.map((appointmentDate) => [
          appointmentDate.appointmentDate,
          appointmentDate,
        ]),
      ),
    [appointmentDates],
  );
  const availableAppointmentDates = useMemo(
    () =>
      appointmentDates
        .filter((appointmentDate) => appointmentDate.isAvailable)
        .map((appointmentDate) => appointmentDate.appointmentDate)
        .sort((left, right) => left.localeCompare(right)),
    [appointmentDates],
  );
  const availableAppointmentDateSet = useMemo(
    () => new Set(availableAppointmentDates),
    [availableAppointmentDates],
  );
  const selectedPlanningDateSet = useMemo(
    () => new Set(selectedPlanningDates),
    [selectedPlanningDates],
  );
  const planningPreviewPersonCounts = useMemo(() => {
    const counts: Record<number, number> = {};

    for (const day of planningPreview?.days ?? []) {
      for (const [personId, count] of Object.entries(
        day.selectedSlotCountsByPersonId,
      )) {
        const numericPersonId = Number(personId);
        counts[numericPersonId] = (counts[numericPersonId] ?? 0) + count;
      }
    }

    return counts;
  }, [planningPreview]);
  const peopleById = useMemo(
    () => new Map(people.map((person) => [person.id, person.displayName])),
    [people],
  );
  const sortedStats = useMemo(() => {
    if (!statsSort) {
      return stats;
    }

    const factor = statsSort.direction === 'desc' ? -1 : 1;
    const { key } = statsSort;

    return [...stats].sort((left, right) => {
      if (key === 'displayName') {
        return left.displayName.localeCompare(right.displayName, 'de') * factor;
      }

      const delta = left[key] - right[key];

      if (delta !== 0) {
        return delta * factor;
      }

      return left.displayName.localeCompare(right.displayName, 'de');
    });
  }, [stats, statsSort]);

  function toggleStatsSort(key: StatsSortKey) {
    setStatsSort((current) => {
      if (current?.key === key) {
        return {
          direction: current.direction === 'desc' ? 'asc' : 'desc',
          key,
        };
      }

      return { direction: key === 'displayName' ? 'asc' : 'desc', key };
    });
  }

  function statsSortIndicator(key: StatsSortKey): string {
    if (statsSort?.key !== key) {
      return '';
    }

    return statsSort.direction === 'desc' ? ' ▾' : ' ▴';
  }
  const monthWednesdays = useMemo(
    () => getWednesdaysForMonth(availabilityMonth),
    [availabilityMonth],
  );
  const visibleWednesdays = dataLoading ? [] : monthWednesdays;
  const previewDays = planningPreviewLoading
    ? []
    : (planningPreview?.days ?? []);

  const filteredPeople = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return people;
    }

    return people.filter((person) =>
      person.displayName.toLowerCase().includes(query),
    );
  }, [people, searchTerm]);

  const selectedPerson = useMemo(
    () => people.find((person) => person.id === selectedPersonId) ?? null,
    [people, selectedPersonId],
  );

  const hasSlotDraftChanges = useMemo(() => {
    return slotsForAdminDate.some((slot) => {
      const draft = slotDrafts[slot.id];
      if (!draft) {
        return false;
      }

      return (
        draft.currentPersonId !== slot.currentPersonId ||
        draft.wantsTrade !== slot.wantsTrade
      );
    });
  }, [slotDrafts, slotsForAdminDate]);

  function seedSlotDrafts(slots: PlanSlot[]) {
    const nextDrafts: Record<number, SlotDraft> = {};

    for (const slot of slots) {
      nextDrafts[slot.id] = {
        currentPersonId: slot.currentPersonId,
        wantsTrade: slot.wantsTrade,
      };
    }

    setSlotDrafts(nextDrafts);
  }

  async function loadAdminData() {
    const [peoplePayload, appointmentDatesPayload, statsPayload, slotsPayload] =
      await Promise.all([
        fetchAdminPeople(),
        fetchAvailableDates(),
        fetchStats(),
        fetchPlanForDate(adminDate),
      ]);

    setPeople(peoplePayload.items);
    setAppointmentDates(appointmentDatesPayload.items);
    setStats(statsPayload.items);
    setSlotsForAdminDate(slotsPayload.items);
    seedSlotDrafts(slotsPayload.items);
  }

  async function runAdminAction(
    action: () => Promise<void>,
    successMessage?: string,
  ) {
    try {
      setLoading(true);
      await action();
      setStatus(successMessage ?? null);
    } catch (error) {
      setStatus(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');

    const syncMobile = () => {
      const nextIsMobile = media.matches;
      setIsMobile(nextIsMobile);

      if (!nextIsMobile) {
        setMobileView('start');
      }
    };

    syncMobile();
    media.addEventListener('change', syncMobile);

    return () => {
      media.removeEventListener('change', syncMobile);
    };
  }, []);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setDataLoading(true);
    loadAdminData()
      .then(() => {
        if (active) {
          setStatus(null);
        }
      })
      .catch(() => {
        if (active) {
          setStatus(
            'Admin-Daten konnten nicht geladen werden. Bitte Zugang prüfen.',
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
          setDataLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [adminDate]);

  useEffect(() => {
    if (people.length === 0) {
      setSelectedPersonId(null);
      return;
    }

    const selectedStillExists =
      selectedPersonId &&
      people.some((person) => person.id === selectedPersonId);

    if (!selectedStillExists) {
      setSelectedPersonId(people[0]?.id ?? null);
    }
  }, [people, selectedPersonId]);

  useEffect(() => {
    setSelectedPlanningDates((current) => {
      const filtered = current.filter((appointmentDate) =>
        availableAppointmentDateSet.has(appointmentDate),
      );

      return filtered.length === current.length ? current : filtered;
    });
  }, [availableAppointmentDateSet]);

  useEffect(() => {
    let active = true;

    if (selectedPlanningDates.length === 0) {
      setPlanningPreview(null);
      setPlanningPreviewError(null);
      setPlanningPreviewLoading(false);
      return () => {
        active = false;
      };
    }

    setPlanningPreviewLoading(true);
    setPlanningPreviewError(null);

    fetchPlanningPreviewForAppointmentDates(selectedPlanningDates)
      .then((preview) => {
        if (!active) {
          return;
        }

        setPlanningPreview(preview as unknown as PlanningPreview);
      })
      .catch((error) => {
        if (!active) {
          return;
        }

        setPlanningPreview(null);
        setPlanningPreviewError(getErrorMessage(error));
      })
      .finally(() => {
        if (active) {
          setPlanningPreviewLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [planningPreviewRefreshKey, selectedPlanningDates]);

  function toggleSelectedPlanningDate(appointmentDate: string) {
    setSelectedPlanningDates((current) =>
      current.includes(appointmentDate)
        ? current.filter((date) => date !== appointmentDate)
        : sortIsoDates([...current, appointmentDate]),
    );
  }

  function selectAllAvailablePlanningDatesForMonth() {
    const monthSelection = monthWednesdays.filter((appointmentDate) =>
      availableAppointmentDateSet.has(appointmentDate),
    );

    setSelectedPlanningDates((current) =>
      sortIsoDates([...current, ...monthSelection]),
    );
  }

  function clearSelectedPlanningDates() {
    setSelectedPlanningDates([]);
  }

  function removePlanForDate(appointmentDate: string) {
    const confirmed = window.confirm(
      `Planung für ${formatDate(appointmentDate)} wirklich entfernen? ` +
        'Alle Zuweisungen dieses Mittwochs werden gelöscht und der Termin wird ' +
        'wieder zur Planung freigegeben.',
    );

    if (!confirmed) {
      return;
    }

    void runAdminAction(async () => {
      await removeAppointmentDatePlan(appointmentDate);
      await loadAdminData();
    }, 'Planung entfernt. Mittwoch ist wieder verfügbar.');
  }

  const peopleSection = (
    <section className="panel" style={{ display: 'grid', gap: 20 }}>
      <div className="panel-head">
        <div>
          <p className="eyebrow">Admin &gt; Personen</p>
          <h2>Personen und Aktivitätszeiträume</h2>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gap: 20,
          gridTemplateColumns: isMobile
            ? '1fr'
            : 'minmax(260px, 340px) minmax(0, 1fr)',
        }}
      >
        <div className="slot-stack">
          <label className="field">
            <span>Suche</span>
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Name suchen"
            />
          </label>

          <div className="slot-stack">
            {dataLoading ? (
              <LoadingIndicator label="Personen werden geladen …" />
            ) : null}
            {filteredPeople.map((person) => {
              const activeToday = isActiveToday(person, today);

              return (
                <button
                  key={person.id}
                  type="button"
                  className="slot-card"
                  style={{
                    textAlign: 'left',
                    borderColor:
                      selectedPersonId === person.id
                        ? 'rgba(21,94,239,0.45)'
                        : undefined,
                  }}
                  onClick={() => setSelectedPersonId(person.id)}
                >
                  <div>
                    <strong>{person.displayName}</strong>
                    <p>
                      {activeToday
                        ? 'Status heute: aktiv'
                        : 'Status heute: inaktiv'}
                    </p>
                  </div>
                  <span className="pill">
                    {activeToday ? 'aktiv' : 'inaktiv'}
                  </span>
                </button>
              );
            })}
            {!dataLoading && filteredPeople.length === 0 ? (
              <p className="muted">Keine Person gefunden.</p>
            ) : null}
          </div>

          <article className="slot-card compact">
            <strong>Neue Person</strong>
            <div className="form-grid" style={{ marginTop: 12 }}>
              <label className="field">
                <span>Name</span>
                <input
                  value={personForm.displayName}
                  onChange={(event) =>
                    setPersonForm((current) => ({
                      ...current,
                      displayName: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field">
                <span>Start aktiv</span>
                <input
                  type="date"
                  value={personForm.startsOn}
                  onChange={(event) =>
                    setPersonForm((current) => ({
                      ...current,
                      startsOn: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field field-full">
                <span>Notiz</span>
                <input
                  value={personForm.notes}
                  onChange={(event) =>
                    setPersonForm((current) => ({
                      ...current,
                      notes: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="button-primary"
                disabled={loading || personForm.displayName.trim() === ''}
                onClick={() =>
                  runAdminAction(async () => {
                    await createAdminPerson(personForm);
                    setPersonForm({
                      displayName: '',
                      notes: '',
                      startsOn: personForm.startsOn,
                    });
                    await loadAdminData();
                  }, 'Person wurde angelegt.')
                }
              >
                Neue Person speichern
              </button>
            </div>
          </article>
        </div>

        <div className="slot-stack">
          <article className="slot-card compact">
            <strong>Detail</strong>
            {selectedPerson ? (
              <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                <span>
                  <strong>Name:</strong> {selectedPerson.displayName}
                </span>
                <span>
                  <strong>Status heute:</strong>{' '}
                  {isActiveToday(selectedPerson, today) ? 'aktiv' : 'inaktiv'}
                </span>
                <span>
                  <strong>Notiz:</strong> {selectedPerson.notes ?? 'keine'}
                </span>
              </div>
            ) : (
              <p className="muted">Bitte eine Person auswählen.</p>
            )}
          </article>

          <article className="slot-card compact">
            <strong>Aktivitätszeiträume</strong>
            {selectedPerson ? (
              <>
                <div className="slot-stack" style={{ marginTop: 12 }}>
                  {selectedPerson.activityPeriods.length === 0 ? (
                    <p className="muted">Noch keine Zeiträume vorhanden.</p>
                  ) : (
                    [...selectedPerson.activityPeriods]
                      .sort((a, b) => (a.startsOn < b.startsOn ? 1 : -1))
                      .map((period) => (
                        <article key={period.id} className="slot-card">
                          <div>
                            <strong>
                              {formatDate(period.startsOn)} -{' '}
                              {period.endsOn
                                ? formatDate(period.endsOn)
                                : 'offen'}
                            </strong>
                            <p>{period.endsOn ? 'beendet' : 'aktiv'}</p>
                          </div>
                          <span className="pill">
                            {period.endsOn ? 'beendet' : 'aktiv'}
                          </span>
                        </article>
                      ))
                  )}
                </div>

                <div className="form-grid" style={{ marginTop: 16 }}>
                  <label className="field">
                    <span>Neuer Zeitraum ab</span>
                    <input
                      type="date"
                      value={activityForm.startsOn}
                      onChange={(event) =>
                        setActivityForm((current) => ({
                          ...current,
                          startsOn: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Aktiven Zeitraum beenden am</span>
                    <input
                      type="date"
                      value={activityForm.endsOn}
                      onChange={(event) =>
                        setActivityForm((current) => ({
                          ...current,
                          endsOn: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <div className="actions" style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="button-primary"
                    disabled={loading}
                    onClick={() =>
                      runAdminAction(async () => {
                        await createActivityPeriod(selectedPerson.id, {
                          startsOn: activityForm.startsOn,
                        });
                        await loadAdminData();
                      }, 'Neuer Aktivitätszeitraum wurde hinzugefügt.')
                    }
                  >
                    Zeitraum hinzufügen
                  </button>
                  <button
                    type="button"
                    className="button-secondary"
                    disabled={loading}
                    onClick={() =>
                      runAdminAction(async () => {
                        const openPeriod = selectedPerson.activityPeriods.find(
                          (period) => period.endsOn === null,
                        );

                        if (!openPeriod) {
                          setStatus('Kein offener Zeitraum vorhanden.');
                          return;
                        }

                        await updateActivityPeriod(openPeriod.id, {
                          endsOn: activityForm.endsOn || today,
                        });
                        await loadAdminData();
                      }, 'Aktiver Zeitraum wurde beendet.')
                    }
                  >
                    Aktiven Zeitraum beenden
                  </button>
                </div>
              </>
            ) : null}
          </article>
        </div>
      </div>
    </section>
  );

  const wednesdayPlanningSection = (
    <section className="panel" style={{ display: 'grid', gap: 20 }}>
      <div className="panel-head">
        <div>
          <p className="eyebrow">Admin &gt; Planung</p>
          <h2>Mittwoche freigeben und konkrete Planung</h2>
        </div>
        <div className="actions" style={{ marginTop: 0 }}>
          <button
            type="button"
            className="button-secondary"
            onClick={selectAllAvailablePlanningDatesForMonth}
          >
            Verfügbare im Monat wählen
          </button>
          <button
            type="button"
            className="button-secondary"
            onClick={clearSelectedPlanningDates}
          >
            Auswahl leeren
          </button>
        </div>
        <MonthStepper
          month={availabilityMonth}
          onChange={setAvailabilityMonth}
          className="panel-head-stepper"
        />
      </div>

      <div
        style={{
          display: 'grid',
          gap: 20,
          gridTemplateColumns: isMobile
            ? '1fr'
            : 'minmax(280px, 1fr) minmax(300px, 1fr)',
        }}
      >
        <div className="slot-stack">
          {dataLoading ? (
            <LoadingIndicator label="Mittwoche werden geladen …" />
          ) : null}
          {visibleWednesdays.map((appointmentDate) => {
            const appointmentDateRecord =
              appointmentDateByDate.get(appointmentDate);
            const isAvailable = appointmentDateRecord?.isAvailable ?? false;
            const isPlanned = appointmentDateRecord?.isPlanned ?? false;
            const isSelected = selectedPlanningDateSet.has(appointmentDate);
            const statusLabel = isPlanned
              ? 'geplant'
              : isAvailable
                ? 'verfügbar'
                : 'nicht verfügbar';

            return (
              <article key={appointmentDate} className="slot-card">
                <div>
                  <strong>{formatDate(appointmentDate)}</strong>
                  <p>{statusLabel}</p>
                </div>
                <div
                  className="slot-actions"
                  style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}
                >
                  {isPlanned ? (
                    <button
                      type="button"
                      className="button-danger"
                      disabled={loading}
                      onClick={() => removePlanForDate(appointmentDate)}
                    >
                      Planung entfernen
                    </button>
                  ) : (
                    <>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={isAvailable}
                          disabled={loading}
                          onChange={(event) =>
                            runAdminAction(async () => {
                              const isAvailableNext = event.target.checked;
                              await setAvailableDateAvailability(
                                appointmentDate,
                                isAvailableNext,
                              );

                              if (!isAvailableNext) {
                                setSelectedPlanningDates((current) =>
                                  current.filter(
                                    (date) => date !== appointmentDate,
                                  ),
                                );
                              }

                              await loadAdminData();
                            }, 'Mittwoch-Verfügbarkeit aktualisiert.')
                          }
                        />
                        verfügbar
                      </label>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={loading || !isAvailable}
                          onChange={() =>
                            toggleSelectedPlanningDate(appointmentDate)
                          }
                        />
                        im Lauf
                      </label>
                    </>
                  )}
                </div>
              </article>
            );
          })}
          {!dataLoading && monthWednesdays.length === 0 ? (
            <p className="muted">Keine Mittwoche im Monat gefunden.</p>
          ) : null}
        </div>

        <article className="slot-card compact">
          <strong>Nächster Planlauf</strong>
          <p className="muted" style={{ marginTop: 8 }}>
            Wähle konkrete, verfügbare Mittwoche aus. Die Vorschau berechnet
            dann den nächsten Lauf direkt aus der Auswahl.
          </p>
          <div className="filters" style={{ marginTop: 12 }}>
            {selectedPlanningDates.length === 0 ? (
              <span className="muted">Noch keine Termine ausgewählt.</span>
            ) : null}
            {selectedPlanningDates.map((appointmentDate) => (
              <button
                key={appointmentDate}
                type="button"
                className="pill active"
                onClick={() => toggleSelectedPlanningDate(appointmentDate)}
              >
                {formatDate(appointmentDate)}
              </button>
            ))}
          </div>

          <div className="metric-grid" style={{ marginTop: 12 }}>
            <article className="metric-card">
              <span>Ausgewählte Termine</span>
              <strong>{selectedPlanningDates.length}</strong>
            </article>
            <article className="metric-card">
              <span>Verfügbare Termine</span>
              <strong>
                {planningPreview?.availableDateCount ??
                  availableAppointmentDates.length}
              </strong>
            </article>
            <article className="metric-card">
              <span>Aktive Personen</span>
              <strong>{planningPreview?.totalActivePeopleCount ?? 0}</strong>
            </article>
            <article className="metric-card">
              <span>Zuweisungen</span>
              <strong>{planningPreview?.totalAssignmentCount ?? 0}</strong>
            </article>
            <article className="metric-card">
              <span>Geplante Slots</span>
              <strong>{planningPreview?.totalPlannedSlotCount ?? 0}</strong>
            </article>
            <article className="metric-card">
              <span>Fehlende Termine</span>
              <strong>{planningPreview?.missingDateCount ?? 0}</strong>
            </article>
          </div>

          <p className="muted" style={{ marginTop: 8 }}>
            Ergebnis:{' '}
            {planningPreviewLoading
              ? 'Vorschau wird berechnet...'
              : planningPreviewError
                ? planningPreviewError
                : planningPreview &&
                    planningPreview.selectedAppointmentDates.length > 0
                  ? planningPreview.missingDateCount === 0
                    ? 'ausreichend planbar'
                    : 'nicht ausreichend'
                  : 'noch keine Auswahl'}
          </p>

          {planningPreview?.selectedAppointmentDates?.length ? (
            <p className="muted">
              Backend-Auswahl:{' '}
              {planningPreview.selectedAppointmentDates
                .map(formatDate)
                .join(', ')}
            </p>
          ) : null}

          {Object.keys(planningPreviewPersonCounts).length > 0 ? (
            <div className="slot-stack" style={{ marginTop: 12 }}>
              {Object.entries(planningPreviewPersonCounts)
                .sort((left, right) => Number(right[1]) - Number(left[1]))
                .slice(0, 6)
                .map(([personId, count]) => (
                  <article key={personId} className="summary-row">
                    <strong>
                      {peopleById.get(Number(personId)) ?? `Person ${personId}`}
                    </strong>
                    <span>geplant: {count}</span>
                    <span />
                  </article>
                ))}
            </div>
          ) : null}

          <div className="actions" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="button-secondary"
              disabled={loading || selectedPlanningDates.length === 0}
              onClick={() =>
                setPlanningPreviewRefreshKey((current) => current + 1)
              }
            >
              Vorschau aktualisieren
            </button>
            <button
              type="button"
              className="button-primary"
              disabled={
                loading ||
                selectedPlanningDates.length === 0 ||
                planningPreviewLoading ||
                planningPreviewError !== null
              }
              onClick={() =>
                runAdminAction(async () => {
                  await planAppointmentDates(selectedPlanningDates);
                  await loadAdminData();
                }, 'Planung gestartet.')
              }
            >
              Planung starten
            </button>
          </div>

          <div className="slot-stack" style={{ marginTop: 16 }}>
            {planningPreviewLoading ? (
              <LoadingIndicator label="Vorschau wird berechnet …" />
            ) : null}
            {previewDays.map((day) => (
              <article key={day.appointmentDate} className="slot-card compact">
                <div className="panel-head" style={{ alignItems: 'center' }}>
                  <div>
                    <strong>{formatDate(day.appointmentDate)}</strong>
                    <p>
                      {day.activePeopleCount} aktive Personen,{' '}
                      {day.plannedSlotCount} Slots
                    </p>
                  </div>
                  <span className="pill">
                    {
                      day.assignments.filter(
                        (assignment) => assignment.personId !== null,
                      ).length
                    }{' '}
                    Zuweisungen
                  </span>
                </div>

                <div className="wednesday-summary">
                  {day.assignments.map((assignment) => (
                    <div
                      key={`${assignment.startsAt}-${assignment.endsAt}-${assignment.appointmentDate}`}
                      className="summary-row"
                    >
                      <strong>
                        {assignment.startsAt} - {assignment.endsAt}
                      </strong>
                      <span>
                        {assignment.slotLabel ??
                          (assignment.personId !== null
                            ? (peopleById.get(assignment.personId) ??
                              `Person ${assignment.personId}`)
                            : 'unbesetzt')}
                      </span>
                      <span>{formatSlotGroupLabel(assignment.slotGroup)}</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
            {!planningPreviewLoading &&
            planningPreview &&
            planningPreview.days.length === 0 ? (
              <p className="muted">
                Für die Auswahl konnten keine Slots berechnet werden.
              </p>
            ) : null}
          </div>
        </article>
      </div>
    </section>
  );

  const slotEditorSection = (
    <section className="panel" style={{ display: 'grid', gap: 16 }}>
      <div className="panel-head">
        <div>
          <p className="eyebrow">Admin &gt; Slot-Editor</p>
          <h2>Manuelle Korrektur pro Mittwoch</h2>
        </div>
        <MonthWednesdayStepper
          date={adminDate}
          onChange={setAdminDate}
          className="panel-head-stepper"
        />
      </div>

      <div className="slot-stack">
        {dataLoading ? (
          <LoadingIndicator label="Slots werden geladen …" />
        ) : null}
        {slotsForAdminDate.map((slot) => {
          const draft = slotDrafts[slot.id] ?? {
            currentPersonId: slot.currentPersonId,
            wantsTrade: slot.wantsTrade,
          };
          const isGroupTraining = isGroupTrainingSlot(slot);

          return (
            <article key={slot.id} className="slot-card">
              <div>
                <strong>
                  {slot.startsAt}-{slot.endsAt}
                </strong>
                <p>
                  Aktuell:{' '}
                  {slotAssignmentLabel(slot, draft.currentPersonId, peopleById)}
                </p>
                <p>Ursprünglich: {slotOriginalAssignmentLabel(slot)}</p>
                <p>Tauschwunsch: {draft.wantsTrade ? 'ja' : 'nein'}</p>
                {isGroupTraining ? (
                  <p className="muted">
                    Dieser Slot ist fest für Gruppenstimmbildung reserviert.
                  </p>
                ) : null}
              </div>

              {isGroupTraining ? null : (
                <div className="slot-actions">
                  <select
                    value={draft.currentPersonId ?? ''}
                    onChange={(event) => {
                      const nextPersonId = event.target.value
                        ? Number(event.target.value)
                        : null;
                      setSlotDrafts((current) => ({
                        ...current,
                        [slot.id]: {
                          ...draft,
                          currentPersonId: nextPersonId,
                        },
                      }));
                    }}
                  >
                    <option value="">unbesetzt</option>
                    {people.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.displayName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() =>
                      setSlotDrafts((current) => ({
                        ...current,
                        [slot.id]: {
                          ...draft,
                          wantsTrade: !draft.wantsTrade,
                        },
                      }))
                    }
                  >
                    {draft.wantsTrade ? 'Tauschwunsch aus' : 'Tauschwunsch an'}
                  </button>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() =>
                      setSlotDrafts((current) => ({
                        ...current,
                        [slot.id]: {
                          ...draft,
                          currentPersonId: null,
                        },
                      }))
                    }
                  >
                    Unbesetzt setzen
                  </button>
                </div>
              )}
            </article>
          );
        })}
        {!dataLoading && slotsForAdminDate.length === 0 ? (
          <p className="muted">Für dieses Datum gibt es keine Slots.</p>
        ) : null}
      </div>

      <label className="field">
        <span>Änderungsnotiz (optional)</span>
        <textarea
          value={editNote}
          onChange={(event) => setEditNote(event.target.value)}
          rows={3}
          placeholder="Optional für deine Dokumentation"
        />
      </label>

      <div className="actions" style={{ marginTop: 0 }}>
        <button
          type="button"
          className="button-primary"
          disabled={loading || !hasSlotDraftChanges}
          onClick={() =>
            runAdminAction(async () => {
              const changes = slotsForAdminDate.filter((slot) => {
                const draft = slotDrafts[slot.id];
                return (
                  draft &&
                  (draft.currentPersonId !== slot.currentPersonId ||
                    draft.wantsTrade !== slot.wantsTrade)
                );
              });

              await Promise.all(
                changes.map((slot) => {
                  const draft = slotDrafts[slot.id]!;
                  return updateAdminSlot(slot.id, {
                    currentPersonId: draft.currentPersonId,
                    wantsTrade: draft.wantsTrade,
                  });
                }),
              );

              if (editNote.trim()) {
                setStatus(
                  'Änderungen gespeichert. Notiz ist nur lokal vermerkt.',
                );
              }

              await loadAdminData();
            }, 'Änderungen gespeichert.')
          }
        >
          Änderung speichern
        </button>
      </div>

      <p className="muted">
        Hinweis: Jede manuelle Änderung wird serverseitig als Admin-Update
        gespeichert.
      </p>
    </section>
  );

  const statsSection = (
    <section className="panel" style={{ display: 'grid', gap: 16 }}>
      <div className="panel-head">
        <div>
          <p className="eyebrow">Admin &gt; Statistik</p>
          <h2>Debt-basierte Fairness und Verteilung</h2>
        </div>
      </div>

      <div className="stats-table">
        <div className="stats-row stats-head">
          <button
            type="button"
            className="stats-sort"
            onClick={() => toggleStatsSort('displayName')}
          >
            Person{statsSortIndicator('displayName')}
          </button>
          <button
            type="button"
            className="stats-sort"
            onClick={() => toggleStatsSort('pastSlots')}
          >
            Termine &lt; heute{statsSortIndicator('pastSlots')}
          </button>
          <button
            type="button"
            className="stats-sort"
            onClick={() => toggleStatsSort('slotDebtUntilToday')}
          >
            Schuld bis heute{statsSortIndicator('slotDebtUntilToday')}
          </button>
          <button
            type="button"
            className="stats-sort"
            onClick={() => toggleStatsSort('futurePlannedSlots')}
          >
            Geplant ≥ heute{statsSortIndicator('futurePlannedSlots')}
          </button>
          <button
            type="button"
            className="stats-sort"
            onClick={() => toggleStatsSort('overallFairnessDebt')}
          >
            Gesamt-Schuld{statsSortIndicator('overallFairnessDebt')}
          </button>
          <span>Früh/Mittel/Spät und Hinweis</span>
        </div>
        {sortedStats.map((item) => (
          <div key={item.personId} className="stats-row">
            <span>{item.displayName}</span>
            <span>{item.pastSlots}</span>
            <span>{formatDebt(item.slotDebtUntilToday)}</span>
            <span>{item.futurePlannedSlots}</span>
            <span>{formatDebt(item.overallFairnessDebt)}</span>
            <span>
              Zeit {formatDebt(item.timeSlotFairnessDebt)} |{' '}
              {item.slotGroupDistribution.frueh}/
              {item.slotGroupDistribution.mittel}/
              {item.slotGroupDistribution.spaet}
              <br />
              {item.fairnessHint} · Zukunft{' '}
              {formatDebt(item.futureOverallFairnessDebt)} ·{' '}
              {item.projectedFairnessTrend}
            </span>
          </div>
        ))}
      </div>

      {dataLoading ? (
        <LoadingIndicator label="Statistik wird geladen …" />
      ) : stats.length === 0 ? (
        <p className="muted">Noch keine Statistikdaten vorhanden.</p>
      ) : null}
    </section>
  );

  function renderCurrentSection() {
    if (isMobile) {
      switch (mobileView) {
        case 'personen':
          return peopleSection;
        case 'mittwoche':
          return wednesdayPlanningSection;
        case 'planung':
          return wednesdayPlanningSection;
        case 'slot-editor':
          return slotEditorSection;
        case 'statistik':
          return statsSection;
        default:
          return null;
      }
    }

    switch (activeTab) {
      case 'personen':
        return peopleSection;
      case 'planung':
        return wednesdayPlanningSection;
      case 'slot-editor':
        return slotEditorSection;
      case 'statistik':
        return statsSection;
      default:
        return peopleSection;
    }
  }

  return (
    <main className="layout">
      <PageHero
        canAccessAdmin={true}
        contextLabel="Bereich"
        contextNote={
          status ??
          (loading ? 'Lade Admin-Daten...' : 'Admin-Funktionen verfügbar')
        }
        contextValue="Administration"
        eyebrow="Admin"
        isAdminPage={true}
        title="Administration"
      />

      {isMobile ? (
        mobileView === 'start' ? (
          <section className="panel" style={{ marginTop: 24 }}>
            <div className="panel-head">
              <div>
                <p className="eyebrow">Admin Start</p>
                <h2>Kachel-Übersicht</h2>
              </div>
            </div>
            <div className="slot-stack">
              <button
                type="button"
                className="button-secondary"
                onClick={() => setMobileView('personen')}
              >
                Personen verwalten
              </button>
              <button
                type="button"
                className="button-secondary"
                onClick={() => setMobileView('mittwoche')}
              >
                Mittwoche freigeben
              </button>
              <button
                type="button"
                className="button-secondary"
                onClick={() => setMobileView('planung')}
              >
                Planung starten
              </button>
              <button
                type="button"
                className="button-secondary"
                onClick={() => setMobileView('slot-editor')}
              >
                Slot-Editor
              </button>
              <button
                type="button"
                className="button-secondary"
                onClick={() => setMobileView('statistik')}
              >
                Statistik
              </button>
            </div>
          </section>
        ) : (
          <section
            className="dashboard-grid"
            style={{ gridTemplateColumns: '1fr' }}
          >
            <div className="actions" style={{ marginTop: 24 }}>
              <button
                type="button"
                className="button-secondary"
                onClick={() => setMobileView('start')}
              >
                Zurück zur Admin-Startseite
              </button>
            </div>
            {renderCurrentSection()}
          </section>
        )
      ) : (
        <>
          <section className="panel" style={{ marginTop: 24 }}>
            <div className="panel-head" style={{ marginBottom: 0 }}>
              <div>
                <p className="eyebrow">Admin-Bereich</p>
                <h2>Abschnitte</h2>
              </div>
            </div>
            <div
              className="actions"
              style={{ marginTop: 12, flexWrap: 'wrap' }}
            >
              <button
                type="button"
                className={
                  activeTab === 'personen'
                    ? 'button-primary'
                    : 'button-secondary'
                }
                onClick={() => setActiveTab('personen')}
              >
                Personen
              </button>
              <button
                type="button"
                className={
                  activeTab === 'planung'
                    ? 'button-primary'
                    : 'button-secondary'
                }
                onClick={() => setActiveTab('planung')}
              >
                Planung
              </button>
              <button
                type="button"
                className={
                  activeTab === 'slot-editor'
                    ? 'button-primary'
                    : 'button-secondary'
                }
                onClick={() => setActiveTab('slot-editor')}
              >
                Slot-Editor
              </button>
              <button
                type="button"
                className={
                  activeTab === 'statistik'
                    ? 'button-primary'
                    : 'button-secondary'
                }
                onClick={() => setActiveTab('statistik')}
              >
                Statistik
              </button>
            </div>
          </section>

          <section
            className="dashboard-grid"
            style={{ gridTemplateColumns: '1fr' }}
          >
            {renderCurrentSection()}
          </section>
        </>
      )}

      {loading && !dataLoading ? (
        <LoadingIndicator label="Aktion wird ausgeführt …" />
      ) : null}
    </main>
  );
}
